/**
 * Component for calculating margin, markup, and discounts based on input values.
 * Allows users to input cost price, selling price, markup percentage, discount percentage, and calculates related values dynamically.
 */

<template>
  <v-container fluid>
    <v-sheet class="d-flex flex-wrap justify-center bg-surface-variant justify-space-evenly ma-2">
      <v-card class="ma-4 pt-2 card-size">
        <v-card-title>Υπολογιστής Περιθωρίου</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="costPrice"
            label="Κόστος Αγοράς"
            type="number"
            suffix="€"
          ></v-text-field>
          <v-text-field
            v-model="sellingPrice"
            label="Τιμή Πώλησης"
            type="number"
            suffix="€"
            v-on:input="changedSellingPrice"
          ></v-text-field>
          <v-text-field
            v-model="margin"
            label="Περιθώριο"
            type="number"
            suffix="%"
            :rules="[rules.marginRange]"
            v-on:input="calculateSellingPrice"
          ></v-text-field>
        </v-card-text>
      </v-card>

      <v-card class="ma-4 pt-2 card-size">
        <v-card-title>Υπολογιστής Προσαύξησης</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="costPrice"
            label="Κόστος Αγοράς"
            type="number"
            suffix="€"
          ></v-text-field>
          <v-text-field
            v-model="markupPercentage"
            label="Ποσοστό Προσαύξησης"
            type="number"
            suffix="%"
            :rules="[rules.markupRange]"
            v-on:input="calculateMarkup"
          ></v-text-field>
          <v-text-field
            v-model="markup"
            label="Προσαύξηση"
            type="number"
            suffix="€"
            v-on:input="calculateMarkupPercentage"
          ></v-text-field>
        </v-card-text>
      </v-card>

      <v-card class="ma-4 pt-2 card-size">
        <v-card-title>Υπολογιστής Έκπτωσης</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="originalPrice"
            label="Αρχική Τιμή"
            type="number"
            suffix="€"
          ></v-text-field>
          <v-text-field
            v-model="discountPercentage"
            label="Ποσοστό Έκπτωσης"
            type="number"
            suffix="%"
            :rules="[rules.discountRange]"
            v-on:input="calculateDiscount"
          ></v-text-field>
          <v-text-field 
            v-model="amountAfterDiscount"
            label="Τιμή μετά την Έκπτωση"
            type="number"
            suffix="€"
            v-on:input="calculateDiscountFromAmount"
          ></v-text-field>
          <v-text-field
            v-model="discount"
            label="Ποσό Έκπτωσης"
            type="number"
            suffix="€"
            v-on:input="calculateDiscountPercentage"
          ></v-text-field>
        </v-card-text>
      </v-card>
    </v-sheet>
  </v-container>
</template>

<script>
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export default {
  data() {
    return {
      costPrice: 0,
      sellingPrice: 0,
      markupPercentage: 0,
      originalPrice: 0,
      discountPercentage: 0,
      margin: null,
      markup: null,
      discount: null,
      amountAfterDiscount: null,
      rules: {
        marginRange: value => {
          if (value >= 0 && value <= 99.999999) {
            return true;
          }
          return 'Το περιθώριο πρέπει να είναι μεταξύ 0 και 99.999999.';
        },
        markupRange: value => {
          if (value >= 0) {
            return true;
          }
          return 'Το ποσοστό προσαύξησης πρέπει να είναι μεγαλύτερο απο 0.';
        },
        discountRange: value => {
          if (value >= 0 && value <= 100) {
            return true;
          }
          return 'Το ποσοστό έκπτωσης πρέπει να είναι μεταξύ 0 και 100.';
        }
      }
    };
  },
  watch: {
    costPrice: debounce(function() {
      this.calculateMargin();
      this.calculateMarkup();
    }, 300), // Adjust the debounce delay as needed

    originalPrice: debounce(function() {
      this.calculateDiscount();
    }, 300),

    discountPercentage: debounce(function() {
      this.calculateDiscount();
    }, 300),
  },
  methods: {
  changedSellingPrice: function() {
      this.calculateMargin();
      this.calculateMarkupSelling();
    },
  calculateMargin() {
  if (this.sellingPrice > 0 && this.costPrice > 0) {
    this.margin = parseFloat((((this.sellingPrice - this.costPrice) / this.sellingPrice) * 100).toFixed(2));
    this.calculateMarkupSelling();
  } else {
    this.margin = null;
  }
  this.calculateMarkup();
},
calculateSellingPrice() {
  if (this.margin > 0 && this.costPrice > 0) {
    this.sellingPrice = parseFloat((((100/(100 - this.margin) ) * this.costPrice)).toFixed(2));
    this.calculateMarkupSelling();
  } else {
    this.sellingPrice = this.costPrice;
  }
},
calculateMarkupPercentage() {
  if (this.costPrice > 0 && this.markup > 0) {
    this.markupPercentage = parseFloat(((((parseFloat(this.costPrice) + parseFloat(this.markup)) / this.costPrice) - 1) * 100).toFixed(2));
    this.sellingPrice = parseFloat((this.costPrice * (1 + this.markupPercentage / 100)).toFixed(2));
    this.calculateMargin();
  } else {
    this.markupPercentage = 0;
  }
},
calculateMarkupSelling() {
  if (this.costPrice > 0) {
    this.markup = parseFloat((this.sellingPrice - this.costPrice).toFixed(2));
    this.markupPercentage = parseFloat(((this.sellingPrice / this.costPrice - 1) * 100).toFixed(2));
  } else {
    this.markup = null;
  }
},
calculateMarkup() {
  if (this.costPrice > 0 && this.markupPercentage > 0) {
    this.markup = parseFloat(((this.costPrice * this.markupPercentage) / 100).toFixed(2));
    this.sellingPrice = parseFloat((this.costPrice * (1 + this.markupPercentage / 100)).toFixed(2));
    this.calculateMargin();
  } else {
    this.markup = null;
  }
},
calculateDiscount() {
  if (this.originalPrice > 0 && this.discountPercentage > 0) {
    this.amountAfterDiscount = parseFloat((this.originalPrice - (this.originalPrice * this.discountPercentage) / 100).toFixed(2));
    this.discount = parseFloat((this.originalPrice - this.amountAfterDiscount).toFixed(2));
  } else {
    this.amountAfterDiscount = null;
  }
},
calculateDiscountPercentage() {
    if (this.originalPrice > 0 && this.discount > 0) {
      this.discountPercentage = parseFloat((this.discount / this.originalPrice * 100).toFixed(2));
      this.amountAfterDiscount = parseFloat((this.originalPrice - (this.originalPrice * this.discountPercentage) / 100).toFixed(2));
    }
  },
calculateDiscountFromAmount() {
    if (this.originalPrice > 0 && this.amountAfterDiscount > 0) {
      this.discountPercentage = parseFloat(((this.originalPrice - this.amountAfterDiscount) / this.originalPrice * 100).toFixed(2));
      this.discount = parseFloat((this.originalPrice - this.amountAfterDiscount).toFixed(2));
    } else {
      this.discount = null;
      this.discountPercentage = null;
    }
  },
  }
};
</script>

<style>
.card-size {
  max-height: 400px; /* Adjust the height as needed */
  width: 300px; /* Adjust the width as needed */
}
</style>